import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import QRCode from "react-qr-code";
import JsBarcode from "jsbarcode";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import { useReactToPrint } from "react-to-print";

const GenerateShipmentLabel = ({ isOpen, onClose, shipment }) => {
  const printRef = useRef();
  const [barcodeDataUrl, setBarcodeDataUrl] = useState(null);

  const InfoRow = ({ text }) => (
    <Box display="flex" gap={0.5} m={0} ml={0.5} lineHeight={1.1}>
      <Typography variant="body2" color="text.primary" fontSize={8}>
        {text || "—"}
      </Typography>
    </Box>
  );

  const truncateDescription = (text, charLimit = 60) => {
    if (!text) return "N/A";
    const trimmed = text.trim();
    if (trimmed.length > charLimit) {
      return trimmed.substring(0, charLimit) + "...";
    }
    return trimmed;
  };

  useEffect(() => {
    if (shipment?.reference || shipment?.shipmentId) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, shipment.reference || shipment.shipmentId, {
          format: "CODE128",
          displayValue: false,
          height: 30,
          width: 1.4,
          margin: 0,
        });
        const dataUrl = canvas.toDataURL("image/png");
        setBarcodeDataUrl(dataUrl);
      } catch (error) {
        console.error("Barcode generation failed:", error);
      }
    }
  }, [shipment]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  if (!shipment) {
    return (
      <BaseModal isOpen={isOpen} onClose={onClose} title="Shipment Label">
        <Typography>No shipment data available</Typography>
      </BaseModal>
    );
  }

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Print Label">
      <Paper
        ref={printRef}
        elevation={1}
        sx={{
          width: "98mm",
          height: "74.676mm",
          padding: "2mm",
          mx: "auto",
          border: "1px solid #000",
          overflow: "hidden",
          boxSizing: "border-box"
        }}
      >
        {/* ========== HEADER – now with shipmentId & route ========== */}
        <div style={{ marginBottom: "2px", position: "relative", minHeight: "58px" }}>
          {/* QR code + date - positioned absolutely so they don't affect layout height */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1px",
            }}
          >
            <QRCode value={shipment.shipmentId} size={46} />
            <span style={{ fontSize: "7px", fontWeight: 600, whiteSpace: "nowrap" }}>
              {shipment.createdAt
                ? new Date(shipment.createdAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                : "N/A"}
            </span>
          </div>

          {/* Top row: entity name (placed above shipmentId so branches can sit close below shipmentId) */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "8px",
              marginBottom: "0px",
              paddingRight: "52px",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                flex: 1,
                wordBreak: "break-word",
                lineHeight: "1.2",
              }}
            >
              {shipment?.entity?.name || "N/A"}
            </div>
          </div>

          {/* Shipment ID centered */}
          <div
            style={{
              fontSize: "13px",
              fontWeight: 700,
              textAlign: "center",
              lineHeight: 1.2,
              marginTop: "2px",
              marginBottom: "1px",
            }}
          >
            {shipment?.shipmentId}
          </div>

          {/* Branch Route - directly below shipmentId with minimal spacing */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0px",
              marginTop: "1px",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                textAlign: "center",
                wordBreak: "break-word",
                lineHeight: "1.2",
              }}
            >
              {`${shipment?.sourceBranch?.name || "N/A"} → `}
              <span style={{ fontWeight: 700, fontSize: "12px" }}>
                {shipment?.destinationBranch?.name || "N/A"}
              </span>
            </div>
            {(shipment?.sourceBranch?.district?.name ||
              shipment?.destinationBranch?.district?.name) && (
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: 500,
                    textAlign: "center",
                    wordBreak: "break-word",
                    lineHeight: "1.2",
                    marginTop: "1px",
                    color: "#444",
                  }}
                >
                  {`${shipment?.sourceBranch?.district?.name || "N/A"} - ${shipment?.destinationBranch?.district?.name || "N/A"
                    }`}
                </div>
              )}
          </div>
        </div>

        <Box my={0.1} borderBottom="1px dotted #000" />
        {/* From / To Section */}
        <Box display="flex" justifyContent="center" fontSize={8} mb={0.25} lineHeight={1}>
          <Box width="48%">
            <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
              From
            </Typography>
            {shipment.vendor ? (
              <>
                <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Name: ${shipment.vendor.shopName}`}</span>} />
                <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Contact: ${shipment.vendor.phone}`}</span>} />
                <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Address: ${shipment.vendor.address}`}</span>} />
                <InfoRow
                  text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0}`}</span>}
                />
              </>
            ) : (
              <>
                <InfoRow
                  text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Name: ${shipment.customerId?.name || shipment.customerId?.customerUser?.name || "N/A"}`}</span>}
                />
                <InfoRow
                  text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Contact: ${shipment.customerId?.phone || shipment.customerId?.customerUser?.phone || "N/A"}`}</span>}
                />
                <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Address: ${shipment.customerId?.address || "N/A"}`}</span>} />
                <InfoRow
                  text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Payer (Sender): Rs. ${shipment.payerDetails?.sender?.totalCharge || 0}`}</span>}
                />
              </>
            )}
          </Box>
          <Box width="48%">
            <Typography fontWeight={700} fontSize={10} gutterBottom sx={{ mb: 0.5 }}>
              To
            </Typography>
            <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Name: ${shipment.receiver?.name || shipment.receiver?.customerUser?.name || "N/A"}`}</span>} />
            <InfoRow
              text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Contact: ${shipment.receiver?.phone || shipment.receiver?.customerUser?.phone || "N/A"}`}</span>}
            />
            <InfoRow text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Address: ${shipment.receiver?.address || "N/A"}`}</span>} />
            <InfoRow
              text={<span className="text-[9px] font-bold" style={{ fontSize: "9px" }}>{`Payer (Receiver): Rs. ${shipment.payerDetails?.receiver?.totalCharge || 0}`}</span>}
            />
          </Box>
        </Box>

        <Box my={0.25} borderBottom="1px dotted #000" />

        {/* Details Section */}
        <Box className="" ml={1} mb={0.25}>
          <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
            <span style={{ fontSize: "8px", fontWeight: 700 }}>Delivery Charge:</span>
            <span style={{ fontSize: "8px", fontWeight: 700 }}>
              Rs. {shipment.shipmentAmount || 0}
            </span>
          </Box>

          {shipment?.weight && (
            <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
              <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Weight:</span>
              <span style={{ fontSize: "8px", fontWeight: 700 }}>
                {shipment.weight} kg
              </span>
            </Box>
          )}
          {shipment?.unit?.value && shipment?.unit?.unitType?.name && (
            <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
              <span style={{ fontSize: "8px", fontWeight: 700 }}>
                {shipment.unit.unitType.name.toLowerCase() === "weight"
                  ? "Shipment Weight:"
                  : "Shipment Unit:"}
              </span>
              <span style={{ fontSize: "8px", fontWeight: 700 }}>
                {shipment.unit.unitType.name.toLowerCase() === "weight"
                  ? `${shipment.unit.value} kg`
                  : `${shipment.unit.value} ${shipment.unit.unitType.name}`}
              </span>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" lineHeight={1.2}>
            <span style={{ fontSize: "8px", fontWeight: 700 }}>Shipment Type:</span>
            <span style={{ fontSize: "8px", fontWeight: 700 }}>
              {shipment?.shipmentDelivery || "N/A"}
            </span>
          </Box>
        </Box>

        <Box borderBottom="1px dotted #000" />

        {/* Description Section */}
        <Box ml={1} my={0.15} lineHeight={1.1}>
          <Box>
            <span style={{ fontSize: "9px", fontWeight: 700 }}>Description:</span>
            <span
              style={{
                fontWeight: 700,
                fontSize: "8px",
                marginLeft: "8px",
                wordBreak: "break-word",
              }}
            >
              {truncateDescription(shipment?.productDescription, 60)}
            </span>
          </Box>
        </Box>

        {/* Instruction Section */}
        <Box ml={1} my={0.15} lineHeight={1.1}>
          <Box>
            <span style={{ fontSize: "9px", fontWeight: 700 }}>Instruction:</span>
            <span
              style={{
                fontWeight: 700,
                fontSize: "8px",
                marginLeft: "8px",
                wordBreak: "break-word",
              }}
            >
              {truncateDescription(shipment?.instruction, 60)}
            </span>
          </Box>
        </Box>

        <Box my={0.1} borderBottom="1px dotted #000" />
        {/* Barcode Section */}
        <Box mt={0.25} textAlign="center" style={{ overflow: 'hidden' }}>
          {barcodeDataUrl ? (
            <>
              <img
                src={barcodeDataUrl}
                alt="Barcode"
                style={{
                  width: "auto",
                  height: "auto",
                  maxHeight: "35px",
                  objectFit: "contain",
                  margin: "0 auto",
                  display: "block"
                }}
              />
              <Typography fontSize={10} fontWeight={600} lineHeight={1} marginTop={0.5}>
                {shipment?.reference || shipment.shipmentId}
              </Typography>
            </>
          ) : (
            <Typography fontSize={8}>Barcode not generated</Typography>
          )}
        </Box>
      </Paper>

      <Box display="flex" justifyContent="flex-end" mt={2} className="no-print">
        <Button variant="contained" onClick={handlePrint}>
          Print
        </Button>
      </Box>

      <style>{`
        @media print {
            @page { margin: 0; }
            .no-print { display: none !important; }
            .MuiPaper-root { 
              width: 103mm; 
              height: 74mm; 
              margin: auto !important;
              border: 1px solid #000;
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            body { 
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
            }
        }
      `}</style>
    </BaseModal>
  );
};

export default GenerateShipmentLabel;
